<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const faq = [
  {
    q: 'Kenapa angka "Omzet Hari Ini" saya masih Rp 0?',
    a: 'Dashboard menghitung transaksi berdasarkan tanggal yang dipilih di kotak "Tanggal" pojok kanan atas. Pastikan tanggalnya benar (default: hari ini). Jika sudah ada transaksi tapi tetap Rp 0, coba klik ulang atau refresh halaman.',
  },
  {
    q: 'Apa bedanya "Laba Kotor" dan "Laba Bersih"?',
    a: 'Laba Kotor = Omzet dikurangi Diskon dan HPP (biaya bahan baku saja). Laba Bersih = Laba Kotor dikurangi Pengeluaran operasional (listrik, gaji, sewa, dll). Laba Bersih adalah angka paling akurat tentang untung sebenarnya.',
  },
  {
    q: 'Kotak "Shift sedang buka" muncul, itu apa?',
    a: 'Itu artinya ada kasir yang sedang membuka shift kasir hari ini dan belum ditutup. Sistem menampilkan jam shift dibuka dan jumlah kas awal (modal kasir) untuk referensi Anda.',
  },
  {
    q: 'Kenapa "Stok Kritis" menunjukkan angka merah?',
    a: 'Itu tandanya ada bahan baku yang stoknya sudah di bawah "Stok Minimum" yang Anda atur. Klik kartu tersebut untuk lihat daftar lengkapnya, dan segera buat Purchase Order jika perlu restok.',
  },
  {
    q: 'Apakah Dashboard bisa dilihat untuk tanggal yang sudah lewat?',
    a: 'Bisa. Ubah kotak "Tanggal" ke tanggal yang Anda mau, dan semua angka (omzet, laba, top produk) akan otomatis menyesuaikan ke tanggal tersebut.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/laporan" class="text-decoration-none text-medium-emphasis text-body-2">
            Laporan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Dashboard</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="primary" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-dashboard-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Dashboard</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Ringkasan performa toko dalam sekejap
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong> Memberi Anda gambaran cepat tentang
          kondisi toko pada tanggal tertentu — tanpa harus buka laporan satu-satu.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="primary" />
            Apa itu Dashboard?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Dashboard</strong> adalah halaman pertama yang sebaiknya Anda
              cek setiap hari — seperti melihat spidometer mobil sebelum jalan jauh.
              Dalam satu layar, Anda langsung tahu: berapa omzet hari ini, berapa
              untung, produk apa yang paling laku, dan bahan baku apa yang
              hampir habis.
            </p>
            <p class="text-body-2 mb-0">
              Semua angka di Dashboard mengikuti tanggal yang Anda pilih di kotak
              <strong>"Tanggal"</strong>. Secara default, Dashboard menampilkan
              data hari ini.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Baca -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-cursor-line" class="me-2" color="success" />
            Cara Membaca Dashboard
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">
                Buka menu <strong>Laporan → Dashboard</strong> dari sidebar kiri
              </li>
              <li class="mb-2">
                Cek kotak <strong>Tanggal</strong> di pojok kanan atas — pastikan
                tanggalnya sesuai yang ingin Anda lihat (default: hari ini)
              </li>
              <li class="mb-2">
                Lihat 4 kartu ringkasan di bagian atas
              </li>
              <li class="mb-0">
                Scroll ke bawah untuk lihat rincian: Ringkasan Keuangan, Top 5 Produk,
                dan Stok Kritis
              </li>
            </ol>

            <p class="text-body-2 mb-2"><strong>Penjelasan 4 kartu ringkasan:</strong></p>
            <VTable class="mb-2">
              <thead>
                <tr>
                  <th style="min-width: 160px">Kartu</th>
                  <th>Artinya</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Omzet Hari Ini</strong></td>
                  <td class="text-body-2 py-2">
                    Total uang masuk dari semua transaksi penjualan pada tanggal
                    yang dipilih. Contoh: Rp 2.450.000
                  </td>
                </tr>
                <tr>
                  <td><strong>Total Transaksi</strong></td>
                  <td class="text-body-2 py-2">
                    Jumlah struk/order yang berhasil dibuat. Contoh: 47 transaksi
                  </td>
                </tr>
                <tr>
                  <td><strong>Laba Kotor</strong></td>
                  <td class="text-body-2 py-2">
                    Omzet dikurangi biaya bahan baku (HPP) dan diskon. Angka di
                    bawahnya menunjukkan persentase margin. Contoh: Rp 1.100.000 (Margin 44.9%)
                  </td>
                </tr>
                <tr>
                  <td><strong>Stok Kritis</strong></td>
                  <td class="text-body-2 py-2">
                    Jumlah bahan baku yang stoknya sudah di bawah batas minimum.
                    Warna hijau = aman, merah = perlu perhatian segera
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Ringkasan Keuangan -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-money-dollar-circle-line" class="me-2" color="info" />
            Blok "Ringkasan Keuangan"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Blok ini menunjukkan alur uang dari atas ke bawah, seperti resep masakan
              yang dikurangi bahan satu per satu:
            </p>
            <VTable density="compact">
              <tbody>
                <tr>
                  <td class="text-body-2 py-2">Omzet</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Total penjualan kotor</td>
                </tr>
                <tr>
                  <td class="text-body-2 py-2">− Diskon</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Potongan harga yang diberikan ke pelanggan</td>
                </tr>
                <tr>
                  <td class="text-body-2 py-2">− HPP</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Biaya bahan baku yang terpakai</td>
                </tr>
                <tr>
                  <td class="text-body-2 py-2 font-weight-bold">= Laba Kotor</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Untung sebelum biaya operasional</td>
                </tr>
                <tr>
                  <td class="text-body-2 py-2">− Pengeluaran</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Biaya operasional (listrik, gaji, dll)</td>
                </tr>
                <tr>
                  <td class="text-body-2 py-2 font-weight-bold">= Laba Bersih</td>
                  <td class="text-body-2 py-2 text-medium-emphasis">Untung yang sesungguhnya masuk kantong</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-trophy-line" class="me-2" color="warning" />
            Blok "Top 5 Produk" & "Stok Kritis"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Top 5 Produk</strong> menampilkan 5 produk paling laris pada
              tanggal yang dipilih, diurutkan dari omzet tertinggi. Berguna untuk
              tahu produk andalan toko Anda.
            </p>
            <p class="text-body-2 mb-0">
              <strong>Stok Kritis</strong> menampilkan daftar bahan baku yang stoknya
              di bawah batas minimum. Jika Anda memiliki akses membuat Purchase
              Order, akan muncul tombol <strong>"Buat Purchase Order"</strong> untuk
              langsung memesan ke supplier.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Cek Dashboard setiap pagi dan malam.</strong> Pagi untuk
                  lihat hasil kemarin, malam untuk pantau progres hari ini sebelum tutup toko.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Jangan panik lihat Laba Kotor kecil di pagi hari.</strong>
                  Wajar karena transaksi belum banyak. Bandingkan di jam yang sama
                  dengan hari-hari sebelumnya.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Segera tindak lanjuti Stok Kritis.</strong> Semakin lama
                  dibiarkan, semakin besar risiko produk tidak bisa dijual karena
                  bahan baku habis.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Laporan Penjualan', sub: 'Lihat detail transaksi per periode', icon: 'ri-line-chart-line', color: 'success', to: '/userguide/laporan/penjualan' },
              { title: 'Laporan Inventory', sub: 'Cek status stok lengkap', icon: 'ri-archive-line', color: 'info', to: '/userguide/laporan/inventory' },
              { title: 'Laporan Laba Rugi', sub: 'Lihat untung/rugi periode tertentu', icon: 'ri-file-chart-line', color: 'warning', to: '/userguide/laporan/laba-rugi' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
